import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, Calendar, Users, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { mockData } from '../mock';
import { scrollToSection } from '../utils/scrollToSection';

const MissionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [selectedImage, setSelectedImage] = useState(null);
  
  // Scroll to top when component mounts or ID changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);
  
  const mission = mockData.projects.find(p => p.id === parseInt(id));

  const handleDonateClick = () => {
    navigate('/donate');
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
  };

  if (!mission) {
    return (
      <div className="min-h-screen pt-32 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Mission Not Found
          </h1>
          <Button onClick={() => navigate('/projects')} className="bg-blue-600 hover:bg-blue-700">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Projects
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 to-white pt-32 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <Button 
            onClick={() => navigate('/projects')} 
            variant="outline"
            className="mb-6"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to All Missions
          </Button>
          
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
                {mission.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-gray-600">
                <div className="flex items-center space-x-2">
                  <MapPin className="h-5 w-5 text-blue-600" />
                  <span style={{ fontFamily: 'Open Sans, sans-serif' }}>{mission.location}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Calendar className="h-5 w-5 text-blue-600" />
                  <span style={{ fontFamily: 'Open Sans, sans-serif' }}>{mission.date}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Users className="h-5 w-5 text-green-600" />
                  <span className="font-semibold text-green-600" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                    {mission.impact}
                  </span>
                </div>
              </div>
            </div>
            <Badge className={`${mission.status === 'completed' ? 'bg-green-500' : 'bg-orange-500'} text-white text-lg px-4 py-2`}>
              {mission.status === 'completed' ? 'Completed' : 'Upcoming'}
            </Badge>
          </div>
          
          <p className="text-xl text-gray-600 max-w-4xl" style={{ fontFamily: 'Open Sans, sans-serif' }}>
            {mission.description}
          </p>
        </div>
      </section>

      {/* Mission Video (if available) */}
      {mission.video && (
        <section className="py-12 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Mission Video
            </h2>
            <div className="max-w-4xl mx-auto">
              <video 
                controls 
                className="w-full rounded-lg shadow-xl"
                poster={mission.thumbnail}
              >
                <source src={mission.video} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </section>
      )}

      {/* Photo Gallery */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Mission Gallery
          </h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {mission.gallery.map((photo, index) => (
              <Card 
                key={index} 
                className="overflow-hidden cursor-pointer transform transition-all hover:scale-105 hover:shadow-xl"
                onClick={() => setSelectedImage(photo)}
              >
                <CardContent className="p-0">
                  <img 
                    src={photo}
                    alt={`${mission.title} - Photo ${index + 1}`}
                    className="w-full h-64 object-cover"
                  />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox for full-size images */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button
            onClick={() => setSelectedImage(null)}
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors"
          >
            <X className="h-8 w-8" />
          </button>
          <img 
            src={selectedImage}
            alt="Full size"
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-blue-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Support Our Future Missions
          </h2>
          <p className="text-xl text-blue-100 mb-8" style={{ fontFamily: 'Open Sans, sans-serif' }}>
            Your donation helps us continue bringing healthcare to communities in need around the world.
          </p>
          <Button 
            onClick={handleDonateClick}
            className="bg-white text-blue-600 hover:bg-gray-100 text-lg px-10 py-6 rounded-lg transition-all transform hover:scale-105"
          >
            Donate Now
          </Button>
        </div>
      </section>
    </div>
  );
};

export default MissionDetail;
